import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import { IoGridOutline, IoListOutline } from "react-icons/io5";
import { CiImageOn } from "react-icons/ci";
import { LuPencil, LuTrash2, LuEye, LuCheck, LuX, LuLink } from "react-icons/lu";
import { GoPlus } from "react-icons/go";

import style from "./ListView.module.css";
import Loader from "../../utils/loader/Loader";
import NoDataFound from "../noDataFound/NoDataFound";
import useUpdateSearchParams from "../../hooks/useUpdateSearchParams";
import { showToast } from "../../utils/Toastify/Toastify";
import {
  adminEpisodeFilterSearch,
  adminFilterSearch,
  adminSeasonFilterSearch,
  creatorEpisodeFilterSearch,
  creatorFilterSearch,
  creatorSeasonFilterSearch,
  getTaxonomyByType,
} from "../../api/HttpApis";

/* Maps the existing endpoint segment → the filter-search "type" the API wants.
   Kept identical to CommonTable so filter behavior is preserved 1:1. */
const ENDPOINT_TYPE = {
  movie: "movie",
  "creator-movie": "movie",
  podcasts: "podcast",
  "short-clips": "short-clip",
  "live-tv-channels": "live-channel",
  "live-streams": "live-stream",
  series: "series",
  users: "users",
  episodes: "episode",
  seasons: "season",
};

/* Lightweight modern dropdown — replaces the heavy Button type="select".
   Accepts the same option shapes (string | {label/name/platform, value/id}). */
const Select = ({ options = [], value, onChange, placeholder }) => {
  const parsed = Array.isArray(options)
    ? options
    : typeof options === "string"
    ? options.split(",").map((o) => o.trim())
    : [];
  return (
    <div className={style.selectWrap}>
      <select
        className={style.select}
        value={value ?? ""}
        onChange={onChange}
      >
        <option value="">{placeholder}</option>
        {parsed.map((opt, i) => {
          const isObj = typeof opt === "object" && opt !== null;
          const label = isObj
            ? opt.label || opt.name || opt.platform || `Option ${i + 1}`
            : opt;
          const val = isObj
            ? opt.value || opt.id || opt.name || opt.platform
            : opt;
          return (
            <option key={i} value={val}>
              {label}
            </option>
          );
        })}
      </select>
      <span className={style.selectChevron}>
        <IoIosArrowDown />
      </span>
    </div>
  );
};

/* Image with graceful fallback: renders `fallback` (initials label or
   placeholder icon) when there's no src or the image fails to load. */
const CardImage = ({ src, alt, fallback }) => {
  const [err, setErr] = useState(false);
  if (!src || err) return fallback;
  return <img src={src} alt={alt || ""} onError={() => setErr(true)} />;
};

const Badge = ({ label, tone = "neutral" }) => {
  const cls =
    tone === "success"
      ? style.badgeSuccess
      : tone === "warning"
      ? style.badgeWarning
      : tone === "danger"
      ? style.badgeDanger
      : style.badgeNeutral;
  return <span className={`${style.badge} ${cls}`}>{label}</span>;
};

// A card may carry a single `badge` or several via `badges`; callers can mix
// both. Nullish entries are dropped so a caller can write
// `badges={[tier, pending && {...}]}` without filtering first.
const badgeList = (c) => [c.badge, ...(c.badges || [])].filter(Boolean);

const ACTION_ICONS = {
  edit: LuPencil,
  delete: LuTrash2,
  view: LuEye,
  accept: LuCheck,
  reject: LuX,
  link: LuLink,
};

/* Renders the small icon-action buttons shared by both layouts. Each action:
   { icon, label, onClick, disabled, tone? }. tone tints the button. */
const ActionButtons = ({ actions = [] }) =>
  actions.map((a, i) => {
    const Icon = ACTION_ICONS[a.icon] || LuPencil;
    const toneCls =
      a.tone === "success"
        ? style.iconBtnSuccess
        : a.tone === "danger"
        ? style.iconBtnDanger
        : "";
    return (
      <button
        key={i}
        type="button"
        className={`${style.iconBtn} ${toneCls}`}
        aria-label={a.ariaLabel || a.label}
        title={a.label}
        disabled={a.disabled}
        onClick={(e) => {
          e.stopPropagation();
          a.onClick?.();
        }}
      >
        <Icon />
      </button>
    );
  });

const ListView = ({
  heading,
  para,
  data = [],
  totalCount = 0,
  onPageChange,
  tableLoading = false,
  button = false,
  handleFilterChange,
  selectedDate,
  selectedRating,
  selectedGenre,
  selectedSeriesFormat,
  contentTypeOptions,
  selectedContentType,
  extraButton = null,
  addLabel,
  onAdd,
  addSecondaryLabel,
  AddSecondaryIcon,
  onAddSecondary,
  stats = [],
  getCard,
  defaultView = "grid",
  rowsOnly = false,
  // Optional status tab bar rendered between the stat tiles and the listing.
  // Each item: { key, label }. `activeTab` is the selected key, `onTabChange`
  // is called with the clicked key.
  tabs = [],
  activeTab,
  onTabChange,
  // Generic filter dropdowns (used by pages like Users that aren't part of the
  // content/series filter system). Each item: { key, placeholder, value, options }.
  customFilters = [],
  // Optional. Anything besides the page that changes what onPageChange returns
  // (filters, search) — a string, or an array/object of the values. Like
  // `activeTab`, a change sends the pager back to page 1 and ListView fetches
  // it via onPageChange, so the page itself never fetches on filter change.
  queryKey,
  // Optional bar rendered just above the listing (e.g. a bulk-selection bar).
  toolbar = null,
  // Optional content shown at the right end of the stat tiles row.
  statsAside = null,
}) => {
  const [searchParams, updateParams] = useUpdateSearchParams();
  const view = rowsOnly
    ? "rows"
    : searchParams.get("view") === "rows"
    ? "rows"
    : defaultView;

  // 24 divides evenly into 2, 3, 4, 6, 8 and 12 grid columns, so the last
  // grid row stays full at common widths (20 left a half-empty row at 8 cols).
  const [rowsPerPage] = useState(24);
  const [currentPage, setCurrentPage] = useState(1);
  const [adminDropDownData, setAdminDropDownData] = useState();
  const [creatorDropDownData, setCreatorDropDownData] = useState();
  const [seriesFormatOptions, setSeriesFormatOptions] = useState([]);

  const role = localStorage.getItem("role");
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const endpoint = pathSegments[pathSegments.length - 1];
  const endpoint2 = pathSegments[pathSegments.length - 2];
  // Seasons/episodes routes end in an id, so fall back to the parent segment.
  const endpointType =
    ENDPOINT_TYPE[endpoint] || ENDPOINT_TYPE[endpoint2] || "";

  const totalPages = Math.ceil((totalCount || 0) / rowsPerPage);

  const setView = (next) => updateParams({ view: next }, { replace: true });

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
  }, [currentPage, totalPages]);

  // A tab or `queryKey` change means a new result set: go back to page 1 and
  // fetch it exactly once. If the pager is already on page 1 there's no page
  // change to trigger the effect below, so fetch directly; otherwise moving to
  // page 1 does it. Compared by value against the previous key (not a "first
  // run" flag) so mount — including StrictMode's double effects — never fires
  // an extra fetch alongside the initial one.
  const resetKey = JSON.stringify([activeTab ?? null, queryKey ?? null]);
  const prevResetKey = useRef(resetKey);
  useEffect(() => {
    if (prevResetKey.current === resetKey) return;
    prevResetKey.current = resetKey;
    if (currentPage !== 1) setCurrentPage(1);
    else onPageChange?.(1, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    onPageChange?.(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  /* Filter dropdown data — same admin/creator fetch as CommonTable. */
  useEffect(() => {
    if (!button) return;
    const fetchDropdowns = async () => {
      try {
        if (role === "Creator") {
          let res;
          if (endpointType === "episode")
            res = await creatorEpisodeFilterSearch(endpointType, endpoint2);
          else if (endpointType === "season")
            res = await creatorSeasonFilterSearch(endpointType, endpoint);
          else res = await creatorFilterSearch(endpointType);
          setCreatorDropDownData(res?.data?.data);
        } else if (role === "Admin") {
          let res;
          if (endpointType === "episode")
            res = await adminEpisodeFilterSearch(endpointType, endpoint2);
          else if (endpointType === "season")
            res = await adminSeasonFilterSearch(endpointType, endpoint);
          else res = await adminFilterSearch(endpointType);
          setAdminDropDownData(res?.data?.data);
        }
      } catch (error) {
        // Show the API's own message (e.g. "Season not found") rather than
        // axios's generic "Request failed with status code 404".
        showToast(error?.response?.data?.message || error?.message, "error");
      }
    };
    fetchDropdowns();
  }, [role, button]);

  /* Fetch series format options from global taxonomy API. */
  useEffect(() => {
    if (endpointType !== "series") return;
    const fetchSeriesFormat = async () => {
      try {
        const res = await getTaxonomyByType("SERIES_FORMAT");
        const raw = res?.data?.data ?? [];
        // Map {key, value} → {label, value} so the Select component renders correctly
        const mapped = raw.map((item) => ({ label: item.key, value: item.value }));
        setSeriesFormatOptions(mapped);
      } catch (error) {
        showToast(error?.message, "error");
      }
    };
    fetchSeriesFormat();
  }, [endpointType]);

  const genreOptions =
    role === "Creator"
      ? creatorDropDownData?.genreNames
      : adminDropDownData?.genreNames;
  const ratingOptions = adminDropDownData?.ratingList;
  const dateOptions =
    role === "Creator"
      ? creatorDropDownData?.dataAdded
      : adminDropDownData?.dataAdded;

  const visiblePages = () => {
    const start = Math.floor((currentPage - 1) / 2) * 2 + 1;
    const pages = [];
    for (let i = start; i < start + 2 && i <= totalPages; i++) pages.push(i);
    return pages;
  };

  const renderCard = (item, index) => {
    const c = getCard ? getCard(item) : {};
    const key = c.id ?? item.id ?? index;

    if (view === "rows") {
      return (
        <div
          key={key}
          className={`${style.rowCard} ${c.selected ? style.rowCardSelected : ""}`}
          onClick={c.onClick}
          role="button"
          tabIndex={0}
        >
          {/* Opt-in per card: pass `onSelect` (+ `selected`) to show a checkbox. */}
          {c.onSelect && (
            <input
              type="checkbox"
              className={style.rowCheck}
              checked={!!c.selected}
              aria-label={`Select ${c.title || "row"}`}
              onClick={(e) => e.stopPropagation()}
              onChange={() => c.onSelect()}
            />
          )}
          <div className={style.rowThumb}>
            <CardImage
              src={c.image}
              alt={c.title}
              fallback={
                c.thumbLabel ? (
                  <span className={style.thumbLabel}>{c.thumbLabel}</span>
                ) : (
                  <span className={style.rowThumbPlaceholder}>
                    <CiImageOn />
                  </span>
                )
              }
            />
          </div>
          <div className={style.rowMain}>
            <div className={style.rowTitle}>{c.title}</div>
            {c.meta && <div className={style.rowMeta}>{c.meta}</div>}
          </div>
          {badgeList(c).map((b, i) => (
            <Badge key={i} label={b.label} tone={b.tone} />
          ))}
          <div className={style.rowActions}>
            <ActionButtons actions={c.actions} />
          </div>
        </div>
      );
    }

    return (
      <div
        key={key}
        className={style.gridCard}
        onClick={c.onClick}
        role="button"
        tabIndex={0}
      >
        <div className={style.poster}>
          <CardImage
            src={c.image}
            alt={c.title}
            fallback={
              c.thumbLabel ? (
                <span className={style.thumbLabelLg}>{c.thumbLabel}</span>
              ) : (
                <span className={style.posterPlaceholder}>
                  <CiImageOn />
                </span>
              )
            }
          />
          {badgeList(c).length > 0 && (
            <div className={style.badgeAbs}>
              {badgeList(c).map((b, i) => (
                <Badge key={i} label={b.label} tone={b.tone} />
              ))}
            </div>
          )}
          {c.actions?.length > 0 && (
            <div className={style.hoverActions}>
              <ActionButtons actions={c.actions} />
            </div>
          )}
        </div>
        <div className={style.gridTitle}>{c.title}</div>
        {c.meta && <div className={style.gridMeta}>{c.meta}</div>}
      </div>
    );
  };

  return (
    <div className={style.container}>
      {/* Header */}
      <div className={style.header}>
        <div className={style.titleWrap}>
          <span className={style.title}>{heading}</span>
          {totalCount > 0 && (
            <span className={style.count}>
              {totalCount.toLocaleString()} total
            </span>
          )}
          {para && <div className={style.para}>{para}</div>}
        </div>

        <div className={style.actions}>
          {customFilters.length > 0 && (
            <div className={style.filters}>
              {customFilters.map((f) => (
                <Select
                  key={f.key}
                  options={f.options}
                  placeholder={f.placeholder}
                  value={f.value}
                  onChange={(e) => f.onChange?.(e.target.value)}
                />
              ))}
            </div>
          )}
          {button && (
            <div className={style.filters}>
              {contentTypeOptions?.length > 0 && (
                <Select
                  options={contentTypeOptions}
                  placeholder="Content Type"
                  value={selectedContentType}
                  onChange={(e) =>
                    handleFilterChange?.("contentType", e.target.value)
                  }
                />
              )}
              {endpointType !== "season" && (
                <Select
                  options={genreOptions}
                  placeholder="Genre"
                  value={selectedGenre}
                  onChange={(e) => handleFilterChange?.("genre", e.target.value)}
                />
              )}
              {role !== "Creator" && (
                <Select
                  options={ratingOptions}
                  placeholder="Rating"
                  value={selectedRating}
                  onChange={(e) =>
                    handleFilterChange?.("rating", e.target.value)
                  }
                />
              )}
              <Select
                options={dateOptions}
                placeholder="Date Added"
                value={selectedDate}
                onChange={(e) => handleFilterChange?.("date", e.target.value)}
              />
              {endpointType === "series" && (
                <Select
                  options={seriesFormatOptions}
                  placeholder="Series Format"
                  value={selectedSeriesFormat}
                  onChange={(e) =>
                    handleFilterChange?.("seriesFormat", e.target.value)
                  }
                />
              )}
            </div>
          )}

          {!rowsOnly && (
            <div className={style.toggle} role="group" aria-label="View mode">
              <button
                type="button"
                className={`${style.toggleBtn} ${
                  view === "grid" ? style.toggleBtnActive : ""
                }`}
                aria-label="Grid view"
                aria-pressed={view === "grid"}
                onClick={() => setView("grid")}
              >
                <IoGridOutline />
              </button>
              <button
                type="button"
                className={`${style.toggleBtn} ${
                  view === "rows" ? style.toggleBtnActive : ""
                }`}
                aria-label="List view"
                aria-pressed={view === "rows"}
                onClick={() => setView("rows")}
              >
                <IoListOutline />
              </button>
            </div>
          )}

          {onAdd && (
            <button type="button" className={style.addBtn} onClick={onAdd}>
              <GoPlus />
              {addLabel || "Add"}
            </button>
          )}

          {onAddSecondary && (
            <button
              type="button"
              className={style.addBtn}
              onClick={onAddSecondary}
            >
              {AddSecondaryIcon ? <AddSecondaryIcon /> : <GoPlus />}
              {addSecondaryLabel || "Add"}
            </button>
          )}

          {extraButton}
        </div>
      </div>

      {/* Stat tiles */}
      {stats.length > 0 && (
        <div className={statsAside ? style.statsRow : undefined}>
          <div className={style.stats}>
            {stats.map((s, i) => (
              <div key={i} className={style.statTile}>
                <div className={style.statLabel}>{s.label}</div>
                <div
                  className={style.statValue}
                  style={s.accent ? { color: s.accent } : undefined}
                >
                  {s.value}
                </div>
                {s.sub && <div className={style.statSub}>{s.sub}</div>}
              </div>
            ))}
          </div>
          {statsAside && <div className={style.statsAside}>{statsAside}</div>}
        </div>
      )}

      {/* Status tabs */}
      {tabs.length > 0 && (
        <div className={style.tabs}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`${style.tab} ${
                activeTab === t.key ? style.tabActive : ""
              }`}
              onClick={() => onTabChange?.(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {toolbar}

      {/* Content */}
      <div className={style.scroll}>
        {tableLoading ? (
          <div className={style.loaderWrap}>
            <Loader />
          </div>
        ) : data && data.length > 0 ? (
          <div className={view === "rows" ? style.rows : style.grid}>
            {data.map((item, i) => renderCard(item, i))}
          </div>
        ) : (
          <div className={style.emptyWrap}>
            <NoDataFound />
          </div>
        )}
      </div>

      {/* Pager — in-flow (not absolute), sits below the content */}
      {totalPages > 1 && (
        <div className={style.pager}>
          <button
            className={style.pagerBtn}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            Previous
          </button>
          {visiblePages().map((p) => (
            <button
              key={p}
              className={`${style.pagerBtn} ${
                currentPage === p ? style.pagerBtnActive : ""
              }`}
              onClick={() => setCurrentPage(p)}
            >
              {p}
            </button>
          ))}
          {totalPages > 2 && <span className={style.pagerEllipsis}>...</span>}
          <button
            className={style.pagerBtn}
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ListView;